import { Button } from "@/components/ui/button";
import { ArrowRight, Star } from "lucide-react";
import { useState, useEffect } from "react";
import OptimizedImage from "@/components/OptimizedImage";
import { trackBookingStart, trackServiceView } from "@/components/GoogleAnalytics";

const HeroSection = ({ onBookingClick }: { onBookingClick: () => void }) => {
  const cities = [
    "Buchanan", "Niles", "St. Joseph", "Benton Harbor", 
    "Berrien Springs", "Stevensville", "Bridgman", "New Buffalo", 
    "Dowagiac", "Edwardsburg", "Cassopolis", "Three Oaks"
  ];
  
  const [currentCityIndex, setCurrentCityIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentCityIndex((prev) => (prev + 1) % cities.length);
        setIsAnimating(false);
      }, 300);
    }, 2000);

    return () => clearInterval(interval);
  }, [cities.length]);

  const getVisibleCities = () => {
    const visible = [];
    for (let i = 0; i < 5; i++) {
      visible.push(cities[(currentCityIndex + i) % cities.length]);
    }
    return visible;
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-32 md:pt-36">
      {/* Background with hero image */}
      <div className="absolute inset-0">
        <OptimizedImage
          src="/lovable-uploads/ad558071-0db2-4301-9fb5-c1235ec6c9b2.png" 
          alt="Professional home organization service in Michigan - beautifully organized luxury living space with custom storage solutions"
          priority={true}
          width={1920}
          height={1080}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background/90"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center fade-in-up">
          {/* Rating Badge */}
          <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-primary text-primary" />
              ))}
            </div>
            <span className="text-sm font-medium text-primary">
              BBB Accredited • Licensed & Insured • 10+ Years Experience
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl font-serif font-light leading-tight mb-6 text-balance">
            Southwest Michigan's{" "}
            <span className="italic text-primary">Professional Organizing</span>{" "}
            <br className="hidden md:block" />
            &{" "}
            <span className="italic text-primary">Cleaning</span>{" "}
            Experts
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-foreground/80 font-light mb-8 max-w-3xl mx-auto text-balance">
            Residential and commercial services designed for clarity, calm, and lasting results across Southwest Michigan.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button
              onClick={() => {
                trackBookingStart();
                onBookingClick();
              }}
              size="lg"
              className="luxury-gradient text-white font-semibold px-12 py-7 text-xl rounded-full shadow-xl hover:scale-105 transition-all group"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => {
                window.open('tel:6167270343', '_self');
                if (typeof window !== 'undefined' && (window as any).gtag) {
                  (window as any).gtag('event', 'click_to_call', {
                    event_category: 'engagement',
                    event_label: 'hero_phone_button'
                  });
                }
              }}
              variant="outline"
              size="lg"
              className="bg-background/80 hover:bg-background text-foreground border-2 border-primary/20 hover:border-primary text-xl px-12 py-7 rounded-full backdrop-blur-sm font-semibold"
            >
              Call Now
            </Button>
          </div>

          {/* Service Areas */}
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-sm text-foreground/70 mb-4">Proudly serving luxury homes and commercial spaces - click a city to book:</p>
            <div className="flex flex-wrap justify-center items-center gap-3 text-sm font-medium">
              {getVisibleCities().map((city, index) => (
                <button 
                  key={`${city}-${currentCityIndex}`}
                  onClick={() => {
                    trackBookingStart();
                    // Store the selected city in sessionStorage so the booking form can use it
                    sessionStorage.setItem('selectedLocation', `${city}, MI`);
                    onBookingClick();
                  }}
                  className={`bg-background/80 border border-primary/20 px-3 py-1 rounded-full text-foreground transition-all duration-300 hover:bg-primary hover:text-white hover:border-primary cursor-pointer ${
                    isAnimating ? 'scale-95 opacity-70' : 'scale-100 opacity-100'
                  }`}
                  style={{ 
                    animationDelay: `${index * 100}ms`,
                    transitionDelay: `${index * 50}ms`
                  }}
                >
                  {city}
                </button>
              ))}
              <button 
                onClick={() => {
                  trackBookingStart();
                  onBookingClick();
                }}
                className="bg-background/80 border border-primary/20 px-3 py-1 rounded-full text-foreground hover:bg-primary hover:text-white hover:border-primary cursor-pointer"
              >
                + More
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-primary/5 rounded-full blur-xl"></div>
      <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-accent/10 rounded-full blur-2xl"></div>
    </section>
  );
};

export default HeroSection;